/**
 * Admin State Hook
 * 
 * Central state management hook for the Admin Panel component.
 * Uses React Query for server state and reducer for UI state.
 */

import { useCallback, useEffect } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { toast } from "@/components/ui/sonner";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useRealtimeChannel } from "@/hooks/useRealtimeChannel";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";
import { useReducerWithDevtools } from "@/lib/useReducerWithDevtools";
import { adminReducer, initialAdminState, adminActions } from "@/reducers/adminReducer";

import type { WaitlistUser, ApprovedUser, AdminTab, AdminStats } from "@/types/state";

// --- Data Fetching Functions ---

const fetchWaitlistedUsers = async (): Promise<WaitlistUser[]> => {
  const { data: waitlistedData, error: waitlistedError } = await supabase
    .from("user_roles")
    .select("user_id, created_at, queue_position")
    .eq("role", "waitlisted")
    .order("queue_position", { ascending: true });

  if (waitlistedError) throw waitlistedError;

  const waitlistedUserIds = waitlistedData?.map((r) => r.user_id) || [];
  if (waitlistedUserIds.length === 0) return [];

  const { data: waitlistedProfiles, error: profilesError } = await supabase
    .from("profiles")
    .select("id, email, email_verified, created_at")
    .in("id", waitlistedUserIds);

  if (profilesError) throw profilesError;

  return waitlistedData?.map((role) => {
    const profile = waitlistedProfiles?.find((p) => p.id === role.user_id);
    return {
      id: role.user_id,
      email: profile?.email || "Unknown",
      created_at: profile?.created_at || role.created_at,
      role_created_at: role.created_at,
      email_verified: profile?.email_verified || false,
      queue_position: role.queue_position,
    };
  }) || [];
};

const fetchApprovedUsers = async (): Promise<ApprovedUser[]> => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const { data: approvedData, error: approvedError } = await supabase
    .from("user_roles")
    .select("user_id, created_at")
    .eq("role", "user")
    .gte("created_at", sevenDaysAgo.toISOString())
    .order("created_at", { ascending: false });

  if (approvedError) throw approvedError;

  const approvedUserIds = approvedData?.map((r) => r.user_id) || [];
  if (approvedUserIds.length === 0) return [];

  const { data: approvedProfiles, error: approvedProfilesError } = await supabase
    .from("profiles")
    .select("id, email, email_verified")
    .in("id", approvedUserIds);

  if (approvedProfilesError) throw approvedProfilesError;

  return approvedData?.map((role) => {
    const profile = approvedProfiles?.find((p) => p.id === role.user_id);
    return {
      id: role.user_id,
      email: profile?.email || "Unknown",
      approved_at: role.created_at,
      email_verified: profile?.email_verified || false,
    };
  }) || [];
};

const fetchTotalApprovedCount = async (): Promise<number> => {
  const { count, error } = await supabase
    .from("user_roles")
    .select("*", { count: "exact", head: true })
    .eq("role", "user");
  if (error) throw error;
  return count || 0;
};

// --- Main Hook ---

export function useAdminState() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  const urlTab = (searchParams.get("tab") || "users") as AdminTab;
  
  const [state, dispatch] = useReducerWithDevtools(
    adminReducer,
    { ...initialAdminState, activeTab: urlTab },
    'AdminPanel'
  );
  
  const { status: realtimeStatus } = useRealtime();
  const isRealtimeConnected = realtimeStatus === 'connected';
  const pollingInterval = realtimeStatus === 'disconnected' ? 30000 : false;

  // --- React Query Hooks ---

  const { data: waitlistUsers = [], isLoading: isLoadingWaitlist } = useQuery({
    queryKey: ['admin', 'waitlistUsers'],
    queryFn: fetchWaitlistedUsers,
    refetchInterval: pollingInterval,
    staleTime: 30000,
  });

  const { data: approvedUsers = [], isLoading: isLoadingApproved } = useQuery({
    queryKey: ['admin', 'approvedUsers'],
    queryFn: fetchApprovedUsers,
    refetchInterval: pollingInterval,
    staleTime: 30000,
  });

  const { data: totalApprovedCount = 0, isLoading: isLoadingStats } = useQuery({
    queryKey: ['admin', 'totalApprovedCount'],
    queryFn: fetchTotalApprovedCount,
    refetchInterval: pollingInterval,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // --- Real-time Subscription ---

  const handleUserRoleChange = useCallback(() => {
    console.log('[Realtime] User role change detected, invalidating admin queries.');
    toast.info("New user activity detected. Refreshing list...");
    queryClient.invalidateQueries({ queryKey: ['admin'] });
  }, [queryClient]);

  useRealtimeChannel({
    channelName: isRealtimeConnected ? 'admin:user-updates' : '',
    type: 'postgres_changes',
    event: 'INSERT',
    schema: 'public',
    table: 'user_roles',
    onMessage: handleUserRoleChange,
  });

  // --- Derived State (Stats) ---

  const stats: AdminStats = {
    totalWaitlisted: waitlistUsers.length,
    recentlyApproved: approvedUsers.length,
    totalApproved: totalApprovedCount,
    approvalRate: (totalApprovedCount + waitlistUsers.length) > 0 
      ? (totalApprovedCount / (totalApprovedCount + waitlistUsers.length)) * 100 
      : 0,
    unverifiedCount: waitlistUsers.filter(u => !u.email_verified).length + approvedUsers.filter(u => !u.email_verified).length,
    unverifiedWaitlisted: waitlistUsers.filter(u => !u.email_verified).length,
    unverifiedApproved: approvedUsers.filter(u => !u.email_verified).length,
  };

  const loading = isLoadingWaitlist || isLoadingApproved || isLoadingStats;

  // --- Manual Refresh Function ---

  const fetchData = useCallback(async (preserveScroll = false) => {
    if (preserveScroll) {
      dispatch(adminActions.setScrollPosition(window.scrollY));
    }
    
    await queryClient.invalidateQueries({ queryKey: ['admin'] });
    
    if (preserveScroll && state.scrollPosition > 0) {
      setTimeout(() => {
        window.scrollTo({
          top: state.scrollPosition,
          behavior: 'instant' as ScrollBehavior
        });
      }, 0);
    }
  }, [queryClient, state.scrollPosition]);

  /**
   * Handle tab changes and persist in URL
   */
  const handleTabChange = useCallback((value: string) => {
    setSearchParams({ tab: value });
    dispatch(adminActions.setActiveTab(value as AdminTab));
  }, [setSearchParams]);

  /**
   * Navigate to dashboard
   */
  const handleGoToDashboard = useCallback(() => {
    navigate(ROUTES.DASHBOARD);
  }, [navigate]);

  /**
   * Navigate to IP management
   */
  const handleGoToIPManagement = useCallback(() => {
    navigate(ROUTES.ADMIN_IP_MANAGEMENT);
  }, [navigate]);

  /**
   * Navigate to prompt management
   */
  const handleGoToPromptManagement = useCallback(() => {
    navigate(ROUTES.ADMIN_PROMPT_MANAGEMENT);
  }, [navigate]);

  /**
   * Navigate to onboarding analytics
   */
  const handleGoToOnboardingAnalytics = useCallback(() => {
    navigate(ROUTES.ADMIN_ONBOARDING_ANALYTICS);
  }, [navigate]);

  /**
   * Sync tab state with URL on mount and URL changes
   */
  useEffect(() => {
    const urlTab = (searchParams.get("tab") || "users") as AdminTab;
    if (urlTab !== state.activeTab) {
      dispatch(adminActions.setActiveTab(urlTab));
    }
  }, [searchParams, state.activeTab]);

  return {
    // UI State
    ...state,
    
    // Server State (from React Query)
    waitlistUsers,
    approvedUsers,
    stats,
    loading,
    
    // Handlers
    fetchData,
    handleTabChange,
    handleGoToDashboard,
    handleGoToIPManagement,
    handleGoToPromptManagement,
    handleGoToOnboardingAnalytics,
  };
}
